import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SingleFormBoxScene,
  CustomTextField,
  CustomAutocomplete,
} from '@/shared/components/ui';
import { PermissionsEnum } from '@/auth/interfaces';
import { yupResolver } from '@hookform/resolvers/yup';
import { ROUTER_PATHS, newComentariosSolicitudCorporativo, useCheckPermissions, formatDateWithTime } from '@/shared';
import { Grid, Typography, Box, Divider, Paper, Button, Stack } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PlaceIcon from '@mui/icons-material/Place';
import SpeedIcon from '@mui/icons-material/Speed';
import { usegetsolicitudcorporativa, usePutSolicitudCorporativa } from '@/shared/actions/erp/solicitudpreventacoorporativo/solicitur-preventa-corporativo.actions'
import { getEstadoConfig, chipStyle, COMPLEJIDAD_OPCIONES } from '../../shared/estado-solicitud';

// Estructura del formulario para el gestor
export type DictamenAdminData = {
  comentario_general: string;
  complejidad: string;
  clientes: {
    id_solicitud_cliente: string;
    sucursales: {
      id_solicitud_sucursal: string;
      comentario: string;
    }[];
  }[];
};

const ComentariosObservacionesPreventa: React.FC = () => {

  useCheckPermissions(PermissionsEnum.api_change_solicitudcorporativa);
  const { id } = useParams();
  const { data } = usegetsolicitudcorporativa(id!);
  const navigate = useNavigate();
  //mutation
  const updateMutation = usePutSolicitudCorporativa({
    navigate,
    returnUrl: ROUTER_PATHS.solicitud_preventa.solicitudVerificarsolicitud
  });

  const form = useForm<DictamenAdminData>({
    resolver: yupResolver(newComentariosSolicitudCorporativo) as any,
    defaultValues: {
      comentario_general: '',
      complejidad: '',
      clientes: [],
    }
  });

  const { control, handleSubmit, reset, formState: { errors } } = form;

  const solicitud = data?.data;

  useEffect(() => {
    if (!solicitud) return;

    const clientes = solicitud.clientes;

    reset({
      comentario_general: solicitud.comentario_general || '',
      complejidad: solicitud.complejidad || '',
      clientes: clientes?.map(cliente => ({
        id_solicitud_cliente: cliente.id_solicitud_cliente!,
        sucursales: cliente?.sucursales?.map(sucursal => ({
          id_solicitud_sucursal: sucursal.id_solicitud_sucursal!,
          comentario: sucursal.comentario || ''
        })) ?? []
      })) ?? []
    });
  }, [solicitud, reset]);


  const gestionar = (formdata: DictamenAdminData, estado: 'REVISADO' | 'RECHAZADO') => {
    updateMutation.mutate({
      id: id!,
      data: {
        comentario_general: formdata.comentario_general,
        complejidad: formdata.complejidad,
        estado_solicitud: estado,
        clientes: formdata.clientes,
      }
    }, {
      onSuccess: () => {
        navigate(ROUTER_PATHS.solicitud_preventa.solicitudVerificarsolicitud);
      }
    });
  };

  const onReject = (formdata: DictamenAdminData) => gestionar(formdata, 'RECHAZADO');
  const onApprove = (formdata: DictamenAdminData) => gestionar(formdata, 'REVISADO');

  const estadoCfg = getEstadoConfig(solicitud?.estado_solicitud);

  return (
    <SingleFormBoxScene
      titlePage={solicitud?.codigo ? `Gestión de Solicitud ${solicitud.codigo}` : 'Gestión de Solicitud'}
      showActionBtns={false}
      showCustomBtns={true}
      customBtns={
        <Grid item xs={12} sx={{ mt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              width: '100%',
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate(-1)}
              sx={{ minWidth: '120px', order: { xs: 2, sm: 1 } }}
            >
              Volver
            </Button>

            <Box sx={{
              display: 'flex',
              gap: 2,
              width: { xs: '100%', sm: 'auto' },
              order: { xs: 1, sm: 2 }
            }}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={handleSubmit(onReject)}
                sx={{ minWidth: '140px' }}
              >
                Rechazar
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="success"
                onClick={handleSubmit(onApprove)}
                sx={{ minWidth: '140px' }}
              >
                Revisado
              </Button>
            </Box>
          </Box>
        </Grid>
      }
    >
      <Grid container spacing={3}>

        {/* RESUMEN DE LA SOLICITUD */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(180deg,#f8f9ff 0%,#ffffff 100%)',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="overline" color="text.secondary">ID Único</Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {solicitud?.codigo ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Usuario solicitante</Typography>
                <Typography variant="subtitle1">{solicitud?.user_name ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Fecha de registro</Typography>
                <Typography variant="subtitle1">
                  {solicitud?.fecha_creacion ? formatDateWithTime(solicitud.fecha_creacion) : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>Estado</Typography>
                <span style={chipStyle(estadoCfg)}>{estadoCfg.label.toUpperCase()}</span>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* COMPLEJIDAD */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Complejidad del requerimiento
          </Typography>
          <Grid container>
            <CustomAutocomplete
              control={control}
              name="complejidad"
              label="Seleccione la complejidad"
              options={COMPLEJIDAD_OPCIONES}
              valueKey="label"
              actualValueKey="value"
              isLoadingData={false}
              error={errors.complejidad as any}
              helperText={(errors.complejidad?.message as string) ?? ''}
              defaultValue={solicitud?.complejidad ?? ''}
              size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              required
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* DETALLE DE SUCURSALES POR CLIENTE */}
        {solicitud?.clientes?.map((cliente, clienteIndex) => (
          <Grid item xs={12} key={cliente?.id_solicitud_cliente}>

            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 2, bgcolor: '#f5f7ff', borderLeft: '5px solid #5e5bdc' }}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1"><b>Cliente:</b> {cliente.cliente_oficina}</Typography>
                  <Typography variant="body2"><b>Ciudad:</b> {cliente.ciudad}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2"><b>Dirección:</b> {cliente.direccion}</Typography>
                  <Typography variant="body2"><b>Contacto:</b> {cliente.contacto_sitio}</Typography>
                  <Typography variant="body2"><b>Teléfono:</b> {cliente.telefono}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {cliente?.sucursales?.map((sucursal, sucursalIndex) => (
              <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }} key={sucursal.id_solicitud_sucursal}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: '#fbfbfb', borderLeft: '4px solid #5e5bdc' }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <StorefrontIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        {sucursal.nombre_sucursal}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SpeedIcon fontSize="small" sx={{ color: '#777' }} />
                      <Typography variant="body2"><b>Velocidad:</b> {sucursal.velocidad} Mbps</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ ml: 0.5 }}><b>Servicio:</b> {sucursal.servicio}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PlaceIcon fontSize="small" sx={{ color: '#777' }} />
                      <Typography variant="body2"><b>Coordenadas:</b> {sucursal.coordenadas}</Typography>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Comentario de la sucursal"
                    name={`clientes.${clienteIndex}.sucursales.${sucursalIndex}.comentario`}
                    control={control}
                    placeholder="Ingrese observaciones..."
                    error={!!errors.clientes?.[clienteIndex]?.sucursales?.[sucursalIndex]?.comentario}
                    helperText={errors.clientes?.[clienteIndex]?.sucursales?.[sucursalIndex]?.comentario?.message ?? ''}
                    shrink
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        ))}

        {/* COMENTARIO GENERAL */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Comentario General
          </Typography>
          <CustomTextField
            label="Observaciones generales"
            name="comentario_general"
            control={control}
            placeholder="Resumen general de la solicitud..."
            error={!!errors.comentario_general}
            helperText={errors.comentario_general?.message ?? ''}
            shrink
          />
        </Grid>

      </Grid>
    </SingleFormBoxScene>
  );
};

export default ComentariosObservacionesPreventa;
